/*
 * FULL-BUILD-LINK: a permanent link that opens the Custom Wheel Builder at Step 3 with a COMPLETE Standard build loaded
 * (both rims, both hubs, spokes, nipples, freehub body, wheel sizes, hole counts). Used by the LoamLabs preconfigured-build pages
 * ("Customize in the builder"). No database and no expiry: the link carries the whole recipe.
 *
 *   /pages/custom-wheel-builder?fullbuild=<base64url JSON>
 *
 * JSON shape (v:1):
 *   { v:1, title:'<page title>', ridingStyle:'mtb', ridingStyleDisplay:'Mountain',
 *     specs:{ frontWheelSize, rearWheelSize, frontSpokeCount, rearSpokeCount, brakeStyle, frontHubSpacing, rearHubSpacing, freehubType },
 *     parts:{ frontRim, rearRim, frontHub, rearHub, frontSpokes, rearSpokes, frontNipples, rearNipples },   // numeric variant ids
 *     vendors:{ frontRim:'Reserve', ... } }                                                                     // optional hints that make the search faster
 *
 * It runs the builder's own code (Step 2 check, Step 3 clicks, validation); nothing here writes the cart. The cart line of a build that
 * matches a preconfigured rule is written by the builder (Path A), exactly as for a hand-made build, so the order data does not change.
 * A part that is retired or not offered for the chosen specs is left out and named in a plain-words notice; nothing crashes.
 *
 * Release 5: the master page's "Add to cart" and "Customize in the builder" both open this link, so the customer always lands on Step 3 with a short note
 * ("Your build is loaded. Add valve stems if you want them, then press Add Build to Cart.") and presses the builder's normal Add Build to Cart. There is no
 * one-click add any more (`&add=1` is ignored). The safety checks stay (addGuard): when the customer presses Add Build to Cart on a build that is still exactly
 * the master's parts, the preconfigured rule must match the master named in the link (`master`), the rule price must equal the price in the link
 * (`expectedCents`), no Studio colour fee, and a sold-out freehub body stops the add. If a check fails NOTHING is added and a plain message is shown. A build the
 * customer has changed is a normal custom build and is added as usual.
 */
(function (global) {
  'use strict';

  var VERSION = 1;
  var CFG = { allowAdd: false }; // one-click add was removed in release 5 (add=1 in a link is ignored)
  var SPEC_KEYS = ['frontWheelSize', 'rearWheelSize', 'frontSpokeCount', 'rearSpokeCount', 'brakeStyle', 'frontHubSpacing', 'rearHubSpacing', 'freehubType'];
  var PART_KEYS = ['frontRim', 'rearRim', 'frontHub', 'rearHub', 'frontSpokes', 'rearSpokes', 'frontNipples', 'rearNipples'];
  var PART_LABEL = { Rim: 'rim', Hub: 'hub', Spokes: 'spokes', Nipples: 'nipples' };
  var TYPE_ID = { Rim: 'rim', Hub: 'hub', Spokes: 'spokes', Nipples: 'nipples' };

  function B() { return global.__loamBuilderInternals; }
  function bs() { var b = B(); return b ? b.buildState : null; }
  function fn(name) { var b = B(); return b && b.fns ? b.fns[name] : null; }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function str(v, max) { return typeof v === 'string' ? v.slice(0, max || 80) : null; }
  function digits(v) { var s = String(v == null ? '' : v).replace(/\D/g, ''); return s ? s.slice(0, 20) : null; }

  /* ------------------------------------------------------------------ encoding */
  function toBase64Url(text) {
    var bytes = new TextEncoder().encode(text), bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function fromBase64Url(s) {
    var b = String(s).replace(/-/g, '+').replace(/_/g, '/'); while (b.length % 4) b += '=';
    var bin = atob(b), bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  }

  // The builder's riding styles are 'mtb', 'gravelroad' and 'hybrid'. Common spellings are accepted; anything else becomes 'mtb' (never a value the builder cannot filter by).
  var STYLE_ALIAS = { mtb: 'mtb', mountain: 'mtb', gravelroad: 'gravelroad', gravel: 'gravelroad', road: 'gravelroad', 'gravel-road': 'gravelroad', gravel_road: 'gravelroad', 'gravel/road': 'gravelroad', hybrid: 'hybrid' };
  function styleOf(v) { var k = typeof v === 'string' ? v.trim().toLowerCase() : ''; return STYLE_ALIAS[k] || 'mtb'; }

  // Whitelist everything that comes from a URL. Returns null unless there is at least a rim and a hub.
  function sanitize(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var specsIn = raw.specs || {}, partsIn = raw.parts || {}, vendorsIn = raw.vendors || {};
    var out = { v: VERSION, master: digits(raw.master), expectedCents: Math.round(Number(raw.expectedCents)) > 0 ? Math.round(Number(raw.expectedCents)) : null, title: str(raw.title, 140), ridingStyle: styleOf(raw.ridingStyle), ridingStyleDisplay: null, specs: {}, parts: {}, vendors: {} };
    if (!out.ridingStyleDisplay) out.ridingStyleDisplay = out.ridingStyle === 'mtb' ? 'Mountain' : (out.ridingStyle === 'hybrid' ? 'Hybrid: Gr/Rd/Mtb' : 'Gravel / Road');
    SPEC_KEYS.forEach(function (k) { var v = str(specsIn[k], 40); if (v) out.specs[k] = v; });
    PART_KEYS.forEach(function (k) { var v = digits(partsIn[k]); if (v) out.parts[k] = v; var h = str(vendorsIn[k], 60); if (h) out.vendors[k] = h; });
    if (!out.parts.frontRim || !out.parts.frontHub) return null;
    if (!out.parts.rearRim) out.parts.rearRim = out.parts.frontRim; // a pair that is the same part on both wheels may name it once
    if (!out.parts.rearHub) out.parts.rearHub = out.parts.frontHub;
    if (out.parts.frontSpokes && !out.parts.rearSpokes) out.parts.rearSpokes = out.parts.frontSpokes;
    if (out.parts.frontNipples && !out.parts.rearNipples) out.parts.rearNipples = out.parts.frontNipples;
    return out;
  }
  function searchFor(raw) { return '?fullbuild=' + toBase64Url(JSON.stringify(raw)); }
  function urlFor(raw, base) { return (base || '/pages/custom-wheel-builder') + searchFor(raw); }
  function fromSearch(search) {
    var q = new URLSearchParams(search != null ? search : (global.location && global.location.search) || ''), v = q.get('fullbuild');
    if (!v) return null;
    try { var clean = sanitize(JSON.parse(fromBase64Url(v))); return clean ? { data: clean, add: false } : null; } catch (e) { return null; }
  }

  /* ------------------------------------------------------------------ state */
  function adopt(clean) {
    var s = bs(); if (!s || !clean) return false;
    s.ridingStyle = clean.ridingStyle; s.ridingStyleDisplay = clean.ridingStyleDisplay; s.buildType = 'Wheel Set';
    s.spokeInterface = 'standard'; // same as a "Build with this" arrival: Standard is preselected
    s.specs = Object.assign({}, s.specs, clean.specs);
    s.fullBuild = { data: clean, notes: [], missing: [], applied: false };
    return true;
  }
  function active() { var s = bs(); return !!(s && s.fullBuild); }

  /* ------------------------------------------------------------------ notices and the loading screen */
  function esc(t) { return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function banner(html, kind) {
    var host = document.getElementById('variant-builder-container'); if (!host) return;
    var el = document.getElementById('full-build-notice');
    if (!el) { el = document.createElement('div'); el.id = 'full-build-notice'; host.insertBefore(el, host.firstChild); }
    el.className = 'full-build-notice' + (kind ? ' is-' + kind : '');
    el.innerHTML = html; el.style.display = html ? '' : 'none';
  }
  function showOverlay() {
    var el = document.getElementById('full-build-overlay');
    if (!el) {
      el = document.createElement('div'); el.id = 'full-build-overlay'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');
      el.innerHTML = '<div class="full-build-overlay__box"><div class="full-build-wait"><span></span></div><p class="full-build-overlay__title">Loading your build&hellip;</p><p class="full-build-overlay__sub">One moment, we are filling in the parts.</p></div>';
      document.body.appendChild(el);
    }
    el.style.display = 'flex';
  }
  function hideOverlay() { var el = document.getElementById('full-build-overlay'); if (el) el.style.display = 'none'; }
  function noticeText(fb) {
    var head = '<strong>Your build is loaded.</strong> Add valve stems if you want them, then press Add Build to Cart. ';
    var notes = (fb.notes || []).map(esc).join(' ');
    return head + (notes ? '<br>' + notes : '');
  }
  function refreshBanner() { var s = bs(); if (!s || !s.fullBuild) { banner(''); return; } banner(noticeText(s.fullBuild), s.fullBuild.notes.length ? 'warn' : 'info'); }

  /* ------------------------------------------------------------------ Step 3: select the parts */
  async function waitFor(test, ms) { var end = Date.now() + (ms || 5000); while (Date.now() < end) { if (test()) return true; await wait(100); } return test(); }
  function containerFor(pos, type) { return document.getElementById(pos + '-' + TYPE_ID[type] + '-options-container'); }
  function filtersFor(pos, type) { return document.getElementById(pos + '-' + TYPE_ID[type] + '-filters'); }
  function findButton(cont, gid) { return cont ? cont.querySelector('.component-variant-button[data-variant-id="' + gid + '"]') : null; }
  async function expandCardsUntil(cont, gid) {
    var heads = cont.querySelectorAll('.component-product-card .product-card-header'), btn = findButton(cont, gid);
    for (var i = 0; i < heads.length && !btn; i++) {
      var card = heads[i].closest ? heads[i].closest('.component-product-card') : null;
      if (card && card.classList && card.classList.contains('expanded')) continue; // already open: clicking again would close it
      heads[i].click(); await wait(120); btn = findButton(cont, gid);
    }
    return btn;
  }
  // Which vendor and model card a variant lives in, from the builder's own product lists (so no card has to be opened one by one).
  function productInfoFor(type, gid) {
    var b = B(), c = b && b.caches; if (!c) return null;
    var list = { Rim: c.rims, Hub: c.hubs, Spokes: c.spokes, Nipples: c.nipples }[type] || [];
    for (var i = 0; i < list.length; i++) { var vs = list[i].variants || []; for (var j = 0; j < vs.length; j++) if (vs[j].id === gid) return { vendor: list[i].vendor, model: list[i].wheelModel || null }; }
    return null;
  }
  // The Standard lists show cards only for the chosen vendor: go to that vendor and model first, then fall back to the hint and "all".
  async function clickVariant(pos, type, id, vendorHint) {
    var cont = containerFor(pos, type); if (!cont) return false;
    var gid = 'gid://shopify/ProductVariant/' + id, btn = findButton(cont, gid);
    if (!btn) {
      var info = productInfoFor(type, gid), filt0 = filtersFor(pos, type), vendor0 = (info && info.vendor) || vendorHint;
      if (vendor0 && filt0) {
        var vb0 = Array.prototype.filter.call(filt0.querySelectorAll('.vendor-filter-btn'), function (b) { return b.dataset.vendor === vendor0; })[0];
        if (vb0 && !(vb0.classList && vb0.classList.contains('active'))) { vb0.click(); await wait(120); }
        cont = containerFor(pos, type);
        btn = findButton(cont, gid);
        if (!btn && info && info.model) {
          var card = Array.prototype.filter.call(cont.querySelectorAll('.component-product-card'), function (c) { return c.dataset && c.dataset.modelName === info.model; })[0];
          var head = card && card.querySelector('.product-card-header');
          if (head && !(card.classList && card.classList.contains('expanded'))) { head.click(); await wait(80); }
          btn = findButton(cont, gid);
        }
      }
    }
    if (!btn) {
      var filt = filtersFor(pos, type), tries = [];
      if (vendorHint) tries.push(vendorHint);
      tries.push('all');
      for (var t = 0; t < tries.length && !btn; t++) {
        var vb = filt ? Array.prototype.filter.call(filt.querySelectorAll('.vendor-filter-btn'), function (b) { return b.dataset.vendor === tries[t]; })[0] : null;
        if (vb) { vb.click(); await wait(250); }
        cont = containerFor(pos, type);
        btn = findButton(cont, gid) || await expandCardsUntil(cont, gid);
      }
    }
    if (!btn) return false;
    btn.click(); return true;
  }
  function selectedId(s, key) { var c = s.components[key]; return c ? digits(c.variantId || c.variantGid) : null; }

  async function applyAtStep3() {
    var s = bs(); if (!s || !s.fullBuild || s.fullBuild.applied) return { ok: true };
    var fb = s.fullBuild, d = fb.data; fb.applied = true;
    var types = ['Rim', 'Hub', 'Spokes', 'Nipples'], positions = ['front', 'rear'];
    function missing(key, text) { if (fb.missing.indexOf(key) < 0) fb.missing.push(key); fb.notes.push(text); }
    try {
      // the rear hub's freehub body is a separate component product: it must be loaded before a hub is clicked (as the System presets do)
      if (s.specs.freehubType) {
        var bb = B();
        if (bb.caches.freehubs && !bb.caches.freehubs.length) {
          if (!bb.componentFetchPromises.freehubs && bb.fns.fetchFreehubComponentProducts) bb.componentFetchPromises.freehubs = bb.fns.fetchFreehubComponentProducts();
          try { if (bb.componentFetchPromises.freehubs) await bb.componentFetchPromises.freehubs; } catch (e) { /* checked below */ }
          await waitFor(function () { return bb.caches.freehubs.length > 0; }, 10000);
        }
      }
      for (var ti = 0; ti < types.length; ti++) {
        var type = types[ti];
        for (var pi = 0; pi < positions.length; pi++) {
          var pos = positions[pi], key = pos + type, id = d.parts[key];
          if (!id) continue;
          if (selectedId(s, key) !== id) {
            var clicked = await clickVariant(pos, type, id, d.vendors[key]);
            if (clicked) await waitFor(function () { return selectedId(s, key) === id; }, 4000);
          }
          if (selectedId(s, key) !== id) missing(key, 'The ' + pos + ' ' + PART_LABEL[type] + ' from this build ' + (type === 'Spokes' || type === 'Nipples' ? 'are' : 'is') + ' not available for the chosen size and hole count right now, so ' + (type === 'Spokes' || type === 'Nipples' ? 'they are' : 'it is') + ' not chosen. Please pick another.');
          await wait(30);
        }
      }
      if (d.specs.freehubType && s.components.rearHub && !s.components.rearComponentFreehub) missing('freehubType', 'The ' + d.specs.freehubType + ' freehub is not offered for this hub, so it is not chosen.');
    } catch (e) {
      if (global.console) global.console.warn('[FULL-BUILD] apply failed: ' + (e && e.message));
      fb.notes.push('Some parts of this build could not be filled in. Please check each section below.');
    }
    refreshBanner();
    return { ok: fb.missing.length === 0 };
  }

  /* ------------------------------------------------------------------ Step 2: use the builder's own spelling of each spec */
  // The Step 2 buttons say '32h', '15x110mm', '12x148mm (Boost)'; a recipe may say '32', '15x110', '12x148'. Pure: used by run() and the tests.
  function norm(v) { return String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9.]/g, ''); }
  function matchValue(wanted, offered) {
    var i, w = norm(wanted); if (!w) return null;
    for (i = 0; i < offered.length; i++) if (offered[i] === wanted) return offered[i];
    for (i = 0; i < offered.length; i++) if (norm(offered[i]) === w) return offered[i];
    var starts = offered.filter(function (o) { var n = norm(o); return n.indexOf(w) === 0 && !/^[0-9.]$/.test(n.charAt(w.length) || 'x'); });
    return starts.length === 1 ? starts[0] : null;
  }
  function groupOf(key) { return key === 'freehubType' ? 'freehub' : key; }
  function resolveSpecs() {
    var s = bs(); if (!s || !s.fullBuild) return;
    Object.keys(s.fullBuild.data.specs).forEach(function (k) {
      var offered = Array.prototype.map.call(document.querySelectorAll('[data-spec-group="' + groupOf(k) + '"]'), function (b) { return b.dataset.value; });
      if (!offered.length) return;
      var hit = matchValue(s.fullBuild.data.specs[k], offered);
      if (hit) s.specs[k] = hit; else { s.specs[k] = null; s.fullBuild.notes.push('This build\'s ' + k.replace(/([A-Z])/g, ' $1').toLowerCase() + ' (' + s.fullBuild.data.specs[k] + ') is not offered by the builder, so it is not chosen. Please pick it in Step 2.'); }
    });
  }

  /* ------------------------------------------------------------------ the safety checks before Add Build to Cart */
  // x = { data, missing:[keys], valid3:bool, match:{masterVariantGid, finalPriceCents}|null, studioFeeCents:number, freehubSoldOut:bool }. finalPriceCents is the MASTER price only:
  // a priced freehub body is its own cart line and never enters the price check. Pure: used by checkAdd() and the tests.
  function addGuard(x) {
    var d = x.data || {};
    if (x.missing && x.missing.length) {
      var colourPart = x.missing.some(function (k) { return /Hub$|Spokes$/.test(k); }); // release 9: a picked hub / spoke colour that could not be chosen (sold out or not offered)
      return { ok: false, reason: colourPart ? 'The hub or spoke colour you chose is not available right now (it may be sold out). Nothing was added to your cart.' : 'One of the parts of this build is not available right now. Nothing was added to your cart.' };
    }
    if (x.freehubSoldOut) return { ok: false, reason: 'The freehub body you chose is sold out right now. Nothing was added to your cart.' };
    if (!x.valid3) return { ok: false, reason: 'The build is not complete yet.' };
    if (!x.match) return { ok: false, reason: 'This build is not available at its LoamLabs price right now (it may be sold out, or a part changed). Nothing was added to your cart.' };
    if (d.master && digits(x.match.masterVariantGid) !== d.master) return { ok: false, reason: 'These parts match a different LoamLabs build than the one you chose. Nothing was added to your cart.' };
    if (x.studioFeeCents > 0) return { ok: false, reason: 'A custom spoke colour was added, so this is no longer the standard LoamLabs build. Nothing was added to your cart.' };
    if (d.expectedCents && Math.round(x.match.finalPriceCents) !== d.expectedCents) return { ok: false, reason: 'The price of this build changed while you were browsing. Nothing was added to your cart.' };
    return { ok: true };
  }
  // The chosen freehub body, looked up in the builder's freehub list: sold out = availableForSale false (unknown counts as available).
  function freehubSoldOut() {
    var s = bs(), b = B(), c = s && s.components && s.components.rearComponentFreehub;
    if (!c || !c.variantId || !b || !b.caches || !b.caches.freehubs) return false;
    for (var i = 0; i < b.caches.freehubs.length; i++) {
      var vs = b.caches.freehubs[i].variants || [];
      for (var j = 0; j < vs.length; j++) if (vs[j].id === c.variantId) return vs[j].availableForSale === false;
    }
    return false;
  }
  // Is the build still exactly the parts the link loaded (every named part chosen, none changed)? Only then is it "the master's build".
  function unchangedFromLink() {
    var s = bs(); if (!s || !s.fullBuild) return false;
    var d = s.fullBuild.data;
    return PART_KEYS.every(function (k) { return !d.parts[k] || selectedId(s, k) === d.parts[k]; });
  }
  // Called by the builder's Add Build to Cart (proceedWithAddToCart) before anything is added. Returns true when the add may go ahead.
  // Builds that did not come from a master link, or that the customer has changed, are normal custom builds and are never blocked here.
  function checkAdd() {
    var s = bs(); if (!s || !s.fullBuild || !s.fullBuild.data.master) return true;
    // Release 9: a part of the master's build that could not be chosen when the link loaded (for example a sold-out hub or spoke colour) and is still not chosen stops the add
    // with a plain message. Nothing is added, and the half-built set is never turned into separate parts at parts prices behind the customer's back.
    var lost = (s.fullBuild.missing || []).filter(function (k) { return s.fullBuild.data.parts[k] && !selectedId(s, k); });
    if (!lost.length && !unchangedFromLink()) return true;
    var fb = s.fullBuild, match = lost.length ? null : (fn('checkForPreconfiguredMatch') ? fn('checkForPreconfiguredMatch')() : null);
    var g = addGuard({ data: fb.data, missing: lost, valid3: true, match: match, studioFeeCents: (global.StandardStudio && global.StandardStudio.feeCents && global.StandardStudio.feeCents()) || 0, freehubSoldOut: freehubSoldOut() });
    if (g.ok) return true;
    var text = 'We could not add this build. ' + g.reason + ' You can change any part to build it as a custom build, or try again later.';
    if (fb.notes.indexOf(text) < 0) fb.notes.push(text);
    refreshBanner();
    try { global.scrollTo(0, 0); } catch (e) { /* none */ }
    if (global.alert) global.alert(text);
    return false;
  }

  /* ------------------------------------------------------------------ entry from the builder's init */
  async function run() {
    var s = bs(); showOverlay();
    try {
      // The builder only starts loading its rims, hubs and the rest when a customer picks a riding style in Step 1; a link skips Step 1, so start the loads now
      // (all of them run side by side) instead of waiting for them in vain.
      var tf = fn('triggerEarlyComponentFetches'); if (tf) tf();
      await fn('showStep')(2, { skipScroll: true });
      var b = B(), pr = b.componentFetchPromises || {};
      var redraw = function () { ['renderStep2', 'updateStep2OptionsVisibility', 'updateStep2VisualFeedback', 'updateSpecAvailabilityCounters', 'updateFreehubLockUI'].forEach(function (n) { var f = fn(n); if (f) f(); }); };
      // Step 2 offers every button until its data is in (afterwards it hides values no part fits). Use the builder's own spelling of each spec NOW, while all buttons
      // exist; with the link's raw values ('28', '12x148') the data would filter the rows to nothing.
      resolveSpecs(); redraw();
      try { await Promise.all([pr.rims, pr.hubs].filter(Boolean)); } catch (e) { /* checked below */ }
      await waitFor(function () { var c = b.caches; return !!(c.rims.length && c.hubs.length); }, 25000);
      redraw();
      if (!fn('validateStep2')()) { hideOverlay(); s.fullBuild.notes.push('Some choices in this build are not available right now, so please check the specs below and continue.'); setTimeout(refreshBanner, 400); return; }
      await fn('showStep')(3, { skipScroll: true }); // the builder calls FullBuild.applyAtStep3() once its lists are loaded
      fn('validateStep3')();
      refreshBanner();
    } catch (e) {
      if (global.console) global.console.warn('[FULL-BUILD] run failed: ' + (e && e.message));
      s.fullBuild.notes.push('Something went wrong while preparing the build. Your choices are filled in as far as possible.');
      setTimeout(refreshBanner, 400);
    }
    hideOverlay();
  }

  function injectStyles() {
    if (!global.document || document.getElementById('full-build-styles')) return;
    var st = document.createElement('style'); st.id = 'full-build-styles';
    st.textContent = '#full-build-overlay{position:fixed;inset:0;z-index:100000;background:rgba(255,255,255,.985);display:none;align-items:center;justify-content:center;text-align:center;padding:24px}' +
      '.full-build-overlay__box{max-width:360px}.full-build-overlay__title{font-size:2.4rem;font-weight:700;margin:18px 0 8px}.full-build-overlay__sub{margin:0;font-size:1.5rem;opacity:.8}' +
      '.full-build-wait{width:44px;height:44px;margin:0 auto;border:4px solid #d9dde3;border-top-color:#111;border-radius:50%;animation:fullbuildspin 0.9s linear infinite}@keyframes fullbuildspin{to{transform:rotate(360deg)}}' +
      '.full-build-notice{margin:0 0 16px;padding:12px 16px;border-radius:6px;background:#eef4ff;border:1px solid #b9cdf2;font-size:1.4rem;line-height:1.5}.full-build-notice.is-warn{background:#fff6e5;border-color:#f0c36d}';
    document.head.appendChild(st);
  }
  if (global.document) { if (document.head) injectStyles(); else document.addEventListener('DOMContentLoaded', injectStyles); }

  global.FullBuild = {
    VERSION: VERSION, CFG: CFG, sanitize: sanitize, searchFor: searchFor, urlFor: urlFor, fromSearch: fromSearch, toBase64Url: toBase64Url, fromBase64Url: fromBase64Url,
    addGuard: addGuard, checkAdd: checkAdd, unchangedFromLink: unchangedFromLink, styleOf: styleOf, adopt: adopt, active: active, matchValue: matchValue, resolveSpecs: resolveSpecs, applyAtStep3: applyAtStep3, run: run, refreshBanner: refreshBanner
  };
})(typeof window !== 'undefined' ? window : globalThis);
